import { validateCPF } from 'validations-br'
import { z } from 'zod'

import { hasProfanity } from '@/shared/lib/string/profanity'

export const registrationNumberSchema = z
  .string()
  .optional()
  .refine(value => !value || validateCPF(value), { message: 'CPF inválido' })

const normalize = (s: string) =>
  String(s ?? '')
    .normalize('NFKC')
    .replace(/\p{Cf}|\u200B|\u200C|\u200D|\u2060/gu, '')
    .replace(/\s+/g, ' ')
    .trim()

const hasUrl = (s: string) => /https?:\/\/|www\./i.test(s)
const hasHandle = (s: string) => /(^|[\s(])@[A-Za-z0-9_\.]{2,}/.test(s)
const hasEmail = (s: string) =>
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i.test(s)
const hasPhone = (s: string) =>
  /(\+\d{1,3}\s?)?(\(?\d{2,3}\)?[\s.-]?)?\d{4,5}[\s.-]?\d{4}\b/.test(s)
const tooManyEmojis = (s: string) => {
  const emojis = s.match(/\p{Extended_Pictographic}/gu) ?? []
  return (
    emojis.length > 1 ||
    (emojis.length === 1 &&
      s.replace(/\p{Extended_Pictographic}/gu, '').trim() === '')
  )
}
const hasAsciiArt = (s: string) => /[|/\\\[\]\(\){}_<>\*\^~]{3,}/.test(s)
const hasLongRepeat = (s: string) => /(.)\1{3,}/u.test(s)
const digitsCount = (s: string) => (s.match(/\d/g) ?? []).length
const allowedChars = (s: string) => /^[\p{L}\p{M}\s.'-]+$/u.test(s)

const shannonEntropy = (s: string) => {
  if (!s) return 0
  const freq = new Map<string, number>()
  for (const ch of s) freq.set(ch, (freq.get(ch) || 0) + 1)
  let H = 0
  for (const [, c] of freq) {
    const p = c / s.length
    H -= p * Math.log2(p)
  }
  return H
}
const vowelRatio = (s: string) => {
  const v = (s.match(/[aeiouáéíóúàâêôãõü]/gi) ?? []).length
  const letters = (s.match(/\p{L}/gu) ?? []).length || 1
  return v / letters
}
const looksGibberish = (s: string) => {
  const noSpaces = !/\s/.test(s)
  const entropy = shannonEntropy(s)
  const vRatio = vowelRatio(s)
  const manyCaps = /[A-Z].*[A-Z].*[A-Z]/.test(s) && /[a-z]/.test(s)
  const hasLeet = /[0-9@$!_]/.test(s)
  return (entropy > 3.8 && vRatio < 0.25 && noSpaces) || (hasLeet && manyCaps)
}

export const nameSchema = z.preprocess(
  v => normalize(String(v ?? '')),
  z
    .string()
    .min(2, 'Nome muito curto.')
    .max(60, 'Nome muito longo.')
    .superRefine((raw, ctx) => {
      if (hasProfanity(raw)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Remova palavrões do nome.'
        })
      }

      if (hasUrl(raw))
        ctx.addIssue({ code: 'custom', message: 'Não use links no nome.' })

      if (hasEmail(raw) || hasPhone(raw) || hasHandle(raw))
        ctx.addIssue({
          code: 'custom',
          message: 'Remova contato/arroba do nome.'
        })

      if (!allowedChars(raw))
        ctx.addIssue({
          code: 'custom',
          message: "Use letras, espaços, '.', '-' e apóstrofo."
        })

      if (tooManyEmojis(raw))
        ctx.addIssue({ code: 'custom', message: 'Evite emojis (máx. 1).' })

      if (hasAsciiArt(raw) || hasLongRepeat(raw))
        ctx.addIssue({
          code: 'custom',
          message: 'Evite símbolos repetidos/ASCII art.'
        })

      if (digitsCount(raw) >= 3)
        ctx.addIssue({ code: 'custom', message: 'Evite números no nome.' })

      if (looksGibberish(raw))
        ctx.addIssue({ code: 'custom', message: 'Não parece nome de pessoa.' })

      if (!/\s/.test(raw) && raw.length < 4)
        ctx.addIssue({
          code: 'custom',
          message: 'Inclua nome e sobrenome (ou algo mais claro).'
        })
    })
)

export type NameForm = { name: z.infer<typeof nameSchema> }
